import { call, fork, put, select, takeLatest } from 'redux-saga/effects'
import { retrieveDates, RetrieveDatesResult } from '../../../../../../common/api/cesta'
import { CITA_PREVIA_SELECTED_TALLER } from '../../../../../../common/context/actions/citaPreviaActions'
import * as CestaActions from '../../../../../../common/context/actions/cestaActions'
import { IAppState } from '../../../../../../common/context/context'
import { CestaState } from '../../../../../../common/context/reducers/cestaReducer'
import { UserState } from '../../../../../../common/context/reducers/userReducer'
import { key as citaPreviaKey } from '../../../../../../common/context/reducers/citaPreviaReducer'
import { selectCesta } from '../../../../../../common/context/selectors/cestaSelectors'
import { selectUserInfo } from '../../../../../../common/context/selectors/userSelectors'
import {
  cestaHasCarNeumaticos,
  shouldSyncCestaTallerToSearch,
} from '../../../../../../shared/cesta/cestaTaller'
import type { SelectedTaller, Taller } from '../../../../../../common/types/Taller'
import { selectTallerSeleccionado } from './tallerSelectors'
import { seleccionarTaller, sincronizarTallerSeleccionado } from './tallerSlice'

const selectCitaPreviaSelectedTaller = (state: IAppState): SelectedTaller =>
  state[citaPreviaKey]?.selectedTaller

function getOrigin(): string {
  return typeof window !== 'undefined' ? window.location.pathname : ''
}

/**
 * Consulta consult-dates (retrieveDates) per al taller ja assignat com a
 * selectedTaller de la cesta, per saber la primera data amb disponibilitat real,
 * i la hi afegeix amb un put a part (UPDATE_SELECTED_TALLER_AVAILABILITY) un cop
 * arriba. Sense la data, CestaHorarios.tsx pot acabar demanant consult-times per
 * a un dia (p.ex. avui + 1) sense hores lliures i l'API respon amb error.
 * Es llança amb `fork` (no bloqueja el put inicial de setSelectedTaller) perquè
 * el buscador de neumàtics refresqui els resultats una sola vegada, en
 * seleccionar el taller, i no una segona vegada quan arriba la data.
 * Igual que fa checkAvailability a CestaTalleres.tsx, és un enriquiment "best
 * effort": si consult-dates falla, es continua amb el taller tal qual.
 */
function* fetchAndPatchAvailabilityDate(
  taller: Taller,
  cestaState: CestaState
) {
  if (!cestaState.products.length) {
    return
  }

  try {
    const userInfo = (yield select(selectUserInfo)) as UserState
    const { nextDays } = (yield call(
      retrieveDates,
      cestaState.products,
      taller.codigo_taller,
      1,
      userInfo,
      cestaState.services,
      cestaState.newVehicleData
    )) as RetrieveDatesResult
    yield put(CestaActions.updateSelectedTallerAvailability(nextDays?.[0]))
  } catch (e) {
    // best effort: es continua sense availabilityDate
  }
}

export function* syncTallerToSearchAndCitaPrevia(taller: Taller) {
  if (!taller?.codigo_taller) {
    return
  }

  const searchTaller = (yield select(selectTallerSeleccionado)) as Taller | null
  if (searchTaller?.codigo_taller !== taller.codigo_taller) {
    yield put(sincronizarTallerSeleccionado(taller))
  }

  const citaTaller = (yield select(
    selectCitaPreviaSelectedTaller
  )) as SelectedTaller
  if (citaTaller?.codigo_taller !== taller.codigo_taller) {
    yield put({
      type: CITA_PREVIA_SELECTED_TALLER,
      payload: { selectedTaller: taller, origin: getOrigin() },
    })
  }
}

function* onCestaTallerSelected(
  action: ReturnType<typeof CestaActions.setSelectedTaller>
) {
  const { selectedTaller } = action.payload
  yield call(syncTallerToSearchAndCitaPrevia, selectedTaller)
}

/** Propaga al catàleg una selecció explícita de taller cap a cesta i cita prèvia. */
function* onSeleccionarTallerFromCatalog(
  action: ReturnType<typeof seleccionarTaller>
) {
  const cestaState = yield select(selectCesta)
  if (!cestaHasCarNeumaticos(cestaState)) {
    return
  }

  const taller = action.payload
  if (!taller?.codigo_taller) {
    return
  }

  if (cestaState.selectedTaller?.codigo_taller !== taller.codigo_taller) {
    yield put(CestaActions.setSelectedTaller(taller as SelectedTaller))
    yield fork(fetchAndPatchAvailabilityDate, taller, cestaState)
  }

  yield call(syncTallerToSearchAndCitaPrevia, taller)
}

/** Copia el taller del buscador cotxe a la cesta només amb pneumàtics de cotxe. */
function* syncTallerToCestaFromSearch() {
  const cestaState = yield select(selectCesta)
  if (cestaState.selectedTaller?.codigo_taller) {
    return
  }

  if (!cestaHasCarNeumaticos(cestaState)) {
    return
  }

  const searchTaller = (yield select(selectTallerSeleccionado)) as Taller | null
  if (!searchTaller?.codigo_taller) {
    return
  }

  yield put(CestaActions.setSelectedTaller(searchTaller as SelectedTaller))
  yield fork(fetchAndPatchAvailabilityDate, searchTaller, cestaState)
}

/**
 * Manté alineats cesta, searchNeumaticos i citaPrevia.
 * El taller del catàleg cotxe només aplica si la cesta té pneumàtics de cotxe.
 */
function* reconcileCestaTallerForCart() {
  const cestaState = yield select(selectCesta)

  if (!cestaHasCarNeumaticos(cestaState)) {
    const citaTaller = (yield select(
      selectCitaPreviaSelectedTaller
    )) as SelectedTaller
    if (!cestaState.selectedTaller?.codigo_taller && citaTaller?.codigo_taller) {
      yield put(CestaActions.setSelectedTaller(citaTaller))
    }
    return
  }

  const cestaTaller = cestaState.selectedTaller
  const searchTaller = (yield select(selectTallerSeleccionado)) as Taller | null

  if (
    shouldSyncCestaTallerToSearch(cestaTaller, searchTaller) &&
    cestaTaller?.codigo_taller
  ) {
    yield call(syncTallerToSearchAndCitaPrevia, cestaTaller)
    return
  }

  if (cestaTaller?.codigo_taller) {
    return
  }

  if (!cestaState.products.length) {
    return
  }

  yield call(syncTallerToCestaFromSearch)
}

function* syncTallerStoresAfterCestaMutation() {
  yield call(reconcileCestaTallerForCart)
}

function* syncTallerFromCestaOnLoad() {
  yield call(syncTallerStoresAfterCestaMutation)
}

function* syncTallerAfterCestaServerUpdate() {
  const cestaState = yield select(selectCesta)
  if (!cestaState.products.length) {
    return
  }
  yield call(syncTallerStoresAfterCestaMutation)
}

export default function* tallerSyncSagas() {
  yield takeLatest(seleccionarTaller.type, onSeleccionarTallerFromCatalog)
  yield takeLatest(CestaActions.ADD_CESTA_SELECTED_TALLER, onCestaTallerSelected)
  yield takeLatest(CestaActions.ADD_PRODUCT_OK, syncTallerStoresAfterCestaMutation)
  yield takeLatest(
    CestaActions.REMOVE_PRODUCT_OK,
    syncTallerStoresAfterCestaMutation
  )
  yield takeLatest(CestaActions.LOAD_CESTA_OK, syncTallerFromCestaOnLoad)
  yield takeLatest(
    CestaActions.UPDATE_CESTA_OK,
    syncTallerAfterCestaServerUpdate
  )
}
